import * as React from 'react'
import { Drawer as DrawerPrimitive } from 'vaul'
import { clsx } from '@unbridaled/shared/utils/clsx'
import { mergeStyles } from '@unbridaled/shared/utils/merge-styles'
import {
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  DrawerContent,
  DrawerTitle
} from './DrawerComponents'

export const BottomDrawerRoot = ({
  isNested,
  ...props
}: React.ComponentProps<typeof DrawerPrimitive.Root> & {
  isNested?: boolean
}): React.ReactNode => {
  const nested = isNested ?? props.nested
  return <DrawerPrimitive.Root {...props} data-slot="root" {...(nested !== undefined ? { nested } : {})} />
}
export const BottomDrawerNestedRoot = DrawerPrimitive.NestedRoot
export const BottomDrawerTrigger = DrawerPrimitive.Trigger
export const BottomDrawerPortal = DrawerPrimitive.Portal
export const BottomDrawerClose = DrawerPrimitive.Close
export const BottomDrawerTitle = DrawerTitle
export const BottomDrawerContent = DrawerContent
export const BottomDrawerDescription = DrawerDescription
export const BottomDrawerFooter = DrawerFooter
export const BottomDrawerOverlay = DrawerOverlay
export const BottomDrawerHeader = (props: React.ComponentProps<typeof DrawerHeader>): React.ReactNode => (
  <DrawerHeader direction="center" {...props} />
)

export type BottomDrawerPanelProps = React.ComponentPropsWithRef<typeof DrawerPrimitive.Content> & {
  maxHeight?: string
  showHandle?: boolean
  asPortal?: boolean
  hideOverlay?: boolean
  overlayProps?: React.ComponentPropsWithoutRef<typeof DrawerOverlay>
  isNested?: boolean
  container?: HTMLElement
}
export const BottomDrawerPanel = React.forwardRef<
  React.ComponentRef<typeof DrawerPrimitive.Content>,
  BottomDrawerPanelProps
>(
  (
    {
      id,
      asPortal = true,
      maxHeight = '90dvh',
      className,
      children,
      showHandle = true,
      hideOverlay,
      overlayProps,
      isNested,
      container,
      ...props
    },
    forwardedRef
  ) => {
    const Container = asPortal ? BottomDrawerPortal : React.Fragment
    const containerProps = asPortal ? { container } : {}
    return (
      <Container {...containerProps}>
        {!hideOverlay && (
          <BottomDrawerOverlay
            {...overlayProps}
            className={clsx(isNested && '!uui-z-modal', overlayProps?.className)}
          />
        )}
        <DrawerPrimitive.Content
          style={mergeStyles({ maxHeight })}
          // id={id ?? 'uui-modal-content'}
          className={clsx(
            'uui-fixed uui-left-0 uui-right-0 uui-bottom-0 uui-z-modal uui-bg-white uui-rounded-t-lg uui-flex uui-flex-col',
            className
          )}
        >
          {showHandle && (
            <div className="uui-pt-4">
              <div className="uui-mx-auto uui-h-[3px] uui-w-[50px] uui-rounded-full uui-bg-gray-800" />
            </div>
          )}
          <div
            ref={forwardedRef}
            id={id ?? 'uui-modal-content'}
            className={clsx('uui-flex-1 uui-overflow-auto hide-scrollbar', className)}
            {...props}
          >
            {children}
          </div>
        </DrawerPrimitive.Content>
      </Container>
    )
  }
)

export type BottomDrawerProps = React.ComponentProps<typeof DrawerPrimitive.Root> &
  BottomDrawerPanelProps & { isNested?: boolean; trigger?: React.ReactNode }

export const BottomDrawer = ({
  id,
  className,
  children,
  maxHeight,
  showHandle,
  asPortal,
  hideOverlay,
  isNested,
  overlayProps,
  trigger,
  container,
  ...props
}: BottomDrawerProps): React.ReactNode => {
  const Root = isNested ? BottomDrawerNestedRoot : BottomDrawerRoot
  return (
    <Root {...props}>
      {/* This is to fix the issue with the drawer moving content up when focusing inputs */}
      {/* Seems to be related to trying to "grab" a clickable element outside the drawer */}
      {/* and doesn't work well when not using the trigger */}
      {trigger}
      <BottomDrawerPanel
        {...(id !== undefined ? { id } : {})}
        {...(container !== undefined ? { container } : {})}
        {...(className !== undefined ? { className } : {})}
        {...(maxHeight !== undefined ? { maxHeight } : {})}
        {...(showHandle !== undefined ? { showHandle } : {})}
        {...(asPortal !== undefined ? { asPortal } : {})}
        {...(hideOverlay !== undefined ? { hideOverlay } : {})}
        {...(isNested !== undefined ? { isNested } : {})}
        {...(overlayProps !== undefined ? { overlayProps } : {})}
      >
        {children}
      </BottomDrawerPanel>
    </Root>
  )
}
